// Builds shareable "preview" links for Naki content (artist, album, track, podcast,
// podcast group, episode). This is the web port of the mobile app's NavigationHelper
// (naki-mobile/packages/router/lib/src/navigation_helper.dart) — the base URL and the
// per-type path shapes are kept in sync with that file. Keep them identical so a link
// copied from the portal opens the same content as one copied from the app.
//
// Link shapes (base = https://preview.naki.audio):
//   artist        /artist/{artistId}
//   album         /album/{albumId}
//   track         /player?albumId={albumId}&trackId={trackId}
//   podcast       /{slug}/{podcastId}                                   slug: podcast|story|shiur
//   podcast group /collection/{podcastId}
//   episode       /{slug}-episode?podcastId={podcastId}&episodeId={episodeId}
//
// Podcast/story/shiur links need the podcast CATEGORY SLUG. When it's missing or
// unknown, the url builders return null so callers can hide/disable the link (the
// mobile helper throws; on the portal a disabled action is friendlier than an error).

export const PREVIEW_BASE_URL = "https://preview.naki.audio";

// The podcast category slugs the preview routes understand (mirrors the switch in
// navigation_helper.dart). Stories and shiurim are podcast categories, not separate types.
export const PODCAST_CATEGORY_SLUGS = ["podcast", "story", "shiur"];

function isKnownPodcastSlug(slug) {
    return PODCAST_CATEGORY_SLUGS.indexOf(slug) !== -1;
}

export function artistPreviewUrl(artistId) {
    if (!artistId) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/artist/${artistId}`;
}

export function albumPreviewUrl(albumId) {
    if (!albumId) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/album/${albumId}`;
}

// A track opens in the player, scoped to its parent album (mirrors copyAlbumTrackUrlToClipboard).
export function albumTrackPreviewUrl(albumId, trackId) {
    if (!albumId || !trackId) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/player?albumId=${albumId}&trackId=${trackId}`;
}

// A single podcast/story/shiur: /{slug}/{podcastId}.
export function podcastPreviewUrl(podcastId, categorySlug) {
    if (!podcastId || !isKnownPodcastSlug(categorySlug)) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/${categorySlug}/${podcastId}`;
}

// A podcast group (parent podcast) always routes through /collection/{podcastId},
// regardless of which podcast category it belongs to.
export function podcastGroupPreviewUrl(podcastId, categorySlug) {
    if (!podcastId || !isKnownPodcastSlug(categorySlug)) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/collection/${podcastId}`;
}

// An episode: /{slug}-episode?podcastId={podcastId}&episodeId={episodeId}.
export function podcastEpisodePreviewUrl(podcastId, episodeId, categorySlug) {
    if (!podcastId || !episodeId || !isKnownPodcastSlug(categorySlug)) {
        return null;
    }
    return `${PREVIEW_BASE_URL}/${categorySlug}-episode?podcastId=${podcastId}&episodeId=${episodeId}`;
}

// Builds the preview url for a producer content-tree node. The node must carry the ids
// each link shape needs beyond its own id:
//   track    → node.albumId   (its parent album)
//   episode  → node.podcastId (its parent podcast)
//   podcast / podcastGroup / episode → node.categorySlug ("podcast"|"story"|"shiur")
// Returns null when the required data is missing (e.g. a podcast with no category slug),
// so the UI can omit the copy-link action for that row.
export function contentNodePreviewUrl(node) {
    if (!node) {
        return null;
    }
    switch (node.type) {
        case "collection": // a music artist
            return artistPreviewUrl(node.id);
        case "album":
            return albumPreviewUrl(node.id);
        case "track":
            return albumTrackPreviewUrl(node.albumId, node.id);
        case "podcastGroup":
            return podcastGroupPreviewUrl(node.id, node.categorySlug);
        case "podcast":
            return podcastPreviewUrl(node.id, node.categorySlug);
        case "episode":
            return podcastEpisodePreviewUrl(node.podcastId, node.id, node.categorySlug);
        default:
            return null;
    }
}
